import {Injectable} from '@angular/core';
import {
  ApplicationAppEntityApplicationRead,
  ApplicationService,
  LocationDecisionService,
  ParcelService,
  RoadOccupationAppEntityRoadOccupationRead,
  RoadOccupationService,
  RoadOccupationStagePurposeFeeService,
  RoadOccupationStageService
} from '../openapi';
import {forkJoin, map, Observable, of, switchMap} from 'rxjs';
import {
  OccupationOfLanePlacementConstructionObjectsFormValue
} from '../containers/applications/application/occupation-of-lane-placement-construction-objects/sections/occupation-of-lane-placement-construction-objects-section/forms/occupation-of-lane-placement-construction-objects.form';
import {
  RoadOccupationFormValue
} from '../containers/applications/application/common/occupation-data-section/forms/road-occupation.form';
import {toNumber} from './helpers';
import {
  AdPlacementRoadOccupationFormValue
} from '../containers/applications/application/placement-advertising/sections/ad-placement-section/forms/ad-placement-road-occupation.form';
import {
  LocationOfDevicesUnrelatedToRoadOccupationFormValue
} from '../containers/applications/application/location-of-devices-unrelated-to-road/sections/location-of-devices-unrelated-to-road-occupation-data-section/forms/location-of-devices-unrelated-to-road-occupation.form';

@Injectable({
  providedIn: 'root'
})
export class RoadOccupationCommonService {
  constructor(
    private applicationService: ApplicationService,
    private roadOccupationService: RoadOccupationService,
    private roadOccupationStageService: RoadOccupationStageService,
    private locationDecisionService: LocationDecisionService,
    private parcelService: ParcelService,
    private roadOccupationStagePurposeFeeService: RoadOccupationStagePurposeFeeService
  ) {
  }

  public updateOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: RoadOccupationFormValue[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({id: v?.uuid || ''}));
    application?.roadOccupations?.forEach(occupation => {
      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({id: parcel?.uuid || ''}));
        });
      });
    });

    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => {
        return forkJoin(
          [...occupations].reverse().map(roadOccupationFormValue => {
            const createDecisionRequests = roadOccupationFormValue?.locationDecisions?.map(locationDecision => this.locationDecisionService.postLocationDecisionLocationDecisionCollection({
              locationDecision
            }).pipe(map(decision => decision.id || '')));
            return (createDecisionRequests?.length ? forkJoin(createDecisionRequests) : of([]))
              .pipe(
                switchMap((decisionIds) => this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
                  roadOccupation: {
                    application: application?.uuid,
                    locationDecisionNumber: roadOccupationFormValue?.locationDecisionNumber,
                    locationDecisionDate: roadOccupationFormValue?.locationDecisionDate || undefined,
                    agreementNumber: roadOccupationFormValue?.agreementNumber || undefined,
                    agreementDate: roadOccupationFormValue?.agreementDate || undefined,
                    purpose: roadOccupationFormValue?.purpose,
                    city: roadOccupationFormValue?.city,
                    occupationType: roadOccupationFormValue?.occupationType,
                    typeOfFailure: roadOccupationFormValue?.typeOfFailure,
                    surfaceViolation: roadOccupationFormValue?.surfaceViolation,
                    workPurpose: roadOccupationFormValue?.workPurpose,
                    locationDecisions: decisionIds
                  }
                }).pipe(
                  switchMap((roadOccupation: RoadOccupationAppEntityRoadOccupationRead) => forkJoin(
                      [...roadOccupationFormValue.stages].reverse().map((stage, index) => {

                        const createParcelsRequests = stage.parcels.length ? forkJoin(stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({parcel}))) : of([]);

                        const purposeFees = stage.purposeFees.filter(v => !!v?.area);

                        const createPurposeFeesRequests = purposeFees.length ? forkJoin(purposeFees.map(fee => this.roadOccupationStagePurposeFeeService.postRoadOccupationStagePurposeFeeRoadOccupationStagePurposeFeeCollection({
                          roadOccupationStagePurposeFee: {
                            width: toNumber(fee.width + '') || undefined,
                            length: toNumber(fee.length + '') || undefined,
                            area: toNumber(fee.area + '') || undefined,
                            purposeFee: fee.feeId
                          }
                        }))) : of([]);


                        return forkJoin([createParcelsRequests, createPurposeFeesRequests]).pipe(
                          switchMap(([parcels, purposeFees]) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                              roadOccupationStage: {
                                ordinalNumber: index + 1,
                                startDate: stage?.startDate || undefined,
                                endDate: stage?.endDate || undefined,
                                dayQuantity: stage?.dayQuantity || undefined,

                                geometry: stage?.geometry || undefined,

                                roadName: stage.roadName,
                                roadNumber: stage.roadNumber,
                                street: stage.street,

                                parcels: parcels.map(v => v?.uuid || '') || [],
                                purposeFees: purposeFees.map(v => v?.uuid || '') || [],

                                roadOccupation: roadOccupation?.uuid
                              }
                            })
                          )
                        );
                      })
                    )
                  )
                ))
              );
          }));
      }),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({id: application?.uuid || ''}))
    );
  }

  public occupationOfLanePlacementConstructionObjects(
    application: ApplicationAppEntityApplicationRead | null,
    occupations: OccupationOfLanePlacementConstructionObjectsFormValue[]
  ): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({id: v?.uuid || ''}));
    application?.roadOccupations?.forEach(occupation => {
      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({id: parcel?.uuid || ''}));
        });
      });
    });
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => {
        return forkJoin(
          [...occupations].reverse().map(roadOccupationFormValue => {
            const createDecisionRequests = roadOccupationFormValue?.locationDecisions?.map(locationDecision => this.locationDecisionService.postLocationDecisionLocationDecisionCollection({
              locationDecision
            }).pipe(map(decision => decision.id || '')));
            return (createDecisionRequests?.length ? forkJoin(createDecisionRequests) : of([]))
              .pipe(
                switchMap((decisionIds) => this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
                  roadOccupation: {
                    application: application?.uuid,
                    locationDecisions: decisionIds
                  }
                }).pipe(
                  switchMap((roadOccupation: RoadOccupationAppEntityRoadOccupationRead) => forkJoin(
                      [...roadOccupationFormValue.stages].reverse().map((stage, index) => {

                        const createParcelsRequests = stage.parcels.length ? forkJoin(stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({parcel}))) : of([]);

                        const purposeFees = stage.purposeFees.filter(v => !!v?.area);

                        const createPurposeFeesRequests = purposeFees.length ? forkJoin(purposeFees.map(fee => this.roadOccupationStagePurposeFeeService.postRoadOccupationStagePurposeFeeRoadOccupationStagePurposeFeeCollection({
                          roadOccupationStagePurposeFee: {
                            width: toNumber(fee.width + '') || undefined,
                            length: toNumber(fee.length + '') || undefined,
                            area: toNumber(fee.area + '') || undefined,
                            purposeFee: fee.feeId
                          }
                        }))) : of([]);


                        return forkJoin([createParcelsRequests, createPurposeFeesRequests]).pipe(
                          switchMap(([parcels, purposeFees]) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                              roadOccupationStage: {
                                ordinalNumber: index + 1,
                                roadName: stage.roadName,
                                roadNumber: stage.roadNumber,
                                street: stage.street,

                                startDate: stage?.startDate || undefined,
                                endDate: stage?.endDate || undefined,
                                dayQuantity: stage?.dayQuantity || undefined,
                                geometry: stage?.geometry?.length ? JSON.stringify(stage?.geometry) : undefined,

                                parcels: parcels.map(v => v?.uuid || '') || [],
                                purposeFees: purposeFees.map(v => v?.uuid || '') || [],

                                roadOccupation: roadOccupation?.uuid
                              }
                            })
                          )
                        );
                      })
                    )
                  )
                ))
              );
          }));
      }),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({id: application?.uuid || ''}))
    );
  }

  public updateAdPlacementOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: AdPlacementRoadOccupationFormValue[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({id: v?.uuid || ''}));
    application?.roadOccupations?.forEach(occupation => {
      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({id: parcel?.uuid || ''}));
        });
      });
    });
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => forkJoin(
          [...occupations].reverse().map((occupationFormValue: AdPlacementRoadOccupationFormValue) => this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
              roadOccupation: {
                application: application?.uuid
              }
            }).pipe(
              switchMap((occupation: RoadOccupationAppEntityRoadOccupationRead) =>
                (occupationFormValue.locationDecisions.length ? forkJoin(occupationFormValue.locationDecisions.map(locationDecision =>
                    this.locationDecisionService.postLocationDecisionLocationDecisionCollection({
                      locationDecision: {
                        ...locationDecision,
                        roadOccupation: occupation.uuid
                      }
                    })
                  )
                ) : of([])).pipe(
                  switchMap(() => forkJoin(
                    [...occupationFormValue.stages].reverse().map((stage, index) => {

                      const createParcelsRequests = stage.parcels.length ? forkJoin(stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({parcel}))) : of([]);

                      const purposeFees = stage.purposeFees.filter(v => !!v?.area);

                      const createPurposeFeesRequests = purposeFees.length ? forkJoin(purposeFees.map(fee => this.roadOccupationStagePurposeFeeService.postRoadOccupationStagePurposeFeeRoadOccupationStagePurposeFeeCollection({
                        roadOccupationStagePurposeFee: {
                          width: toNumber(fee.width + '') || undefined,
                          length: toNumber(fee.length + '') || undefined,
                          area: toNumber(fee.area + '') || undefined,
                          quantity: toNumber(fee.quantity + '') || undefined,
                          purposeFee: fee.feeId
                        }
                      }))) : of([]);

                      return forkJoin([createParcelsRequests, createPurposeFeesRequests]).pipe(
                        switchMap(([parcels, purposeFees]) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                            roadOccupationStage: {
                              ordinalNumber: index + 1,
                              startDate: stage?.startDate || undefined,
                              endDate: stage?.endDate || undefined,
                              dayQuantity: stage.dayQuantity || 0,

                              adKind: stage.adKind || undefined,
                              adLightingType: stage.adLightingType || undefined,
                              adType: stage.adType || undefined,
                              adMountingStructure: stage.adMountingStructure,

                              geometry: stage?.geometry?.length ? JSON.stringify(stage?.geometry) : undefined,

                              roadName: stage.roadName,
                              roadNumber: stage.roadNumber,
                              street: stage.street || '',

                              parcels: parcels.map(v => v?.uuid || '') || [],
                              purposeFees: purposeFees.map(v => v?.uuid || '') || [],

                              roadOccupation: occupation?.uuid
                            }
                          })
                        )
                      );
                    })
                  ))
                )
              )
            )
          )
        )
      ),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({id: application?.uuid || ''}))
    );
  }

  public updateLocationOfDevicesUnrelatedToRoadOccupations(application: ApplicationAppEntityApplicationRead | null, occupations: LocationOfDevicesUnrelatedToRoadOccupationFormValue[]): Observable<ApplicationAppEntityApplicationRead> {
    const deleteRequests = (application?.roadOccupations || []).map((v: any) => this.roadOccupationService.deleteRoadOccupationRoadOccupationItem({id: v?.uuid || ''}));
    application?.roadOccupations?.forEach(occupation => {
      occupation?.stages?.forEach(stage => {
        stage?.parcels?.forEach(parcel => {
          deleteRequests.push(this.parcelService.deleteParcelParcelItem({id: parcel?.uuid || ''}));
        });
      });
    });
    return (deleteRequests.length ? forkJoin(deleteRequests) : of([])).pipe(
      switchMap(() => forkJoin(
          [...occupations].reverse().map(occupationFormValue => {
            const createLocationDecisions = occupationFormValue.locationDecisions.map(locationDecision => this.locationDecisionService.postLocationDecisionLocationDecisionCollection({
              locationDecision
            }).pipe(map(decision => decision?.id || '')));
            return forkJoin(createLocationDecisions).pipe(
              switchMap((locationDecisions) => {
                return this.roadOccupationService.postRoadOccupationRoadOccupationCollection({
                  roadOccupation: {
                    application: application?.uuid,
                    deviceType: occupationFormValue.deviceType,
                    locationDecisions
                  }
                }).pipe(
                  switchMap((occupation) => forkJoin(
                    [...occupationFormValue.stages].reverse().map((stage, index) => {

                      const createParcelsRequests = stage.parcels.length ? forkJoin(stage.parcels.map(parcel => this.parcelService.postParcelParcelCollection({parcel}))) : of([]);

                      const purposeFees = stage.purposeFees.filter(v => !!v?.area);

                      const createPurposeFeesRequests = purposeFees.length ? forkJoin(purposeFees.map(fee => this.roadOccupationStagePurposeFeeService.postRoadOccupationStagePurposeFeeRoadOccupationStagePurposeFeeCollection({
                        roadOccupationStagePurposeFee: {
                          width: toNumber(fee.width + '') || undefined,
                          length: toNumber(fee.length + '') || undefined,
                          area: toNumber(fee.area + '') || undefined,
                          purposeFee: fee.feeId
                        }
                      }))) : of([]);


                      return forkJoin([createParcelsRequests, createPurposeFeesRequests]).pipe(
                        switchMap(([parcels, purposeFees]) => this.roadOccupationStageService.postRoadOccupationStageRoadOccupationStageCollection({
                            roadOccupationStage: {
                              startDate: stage?.startDate || undefined,
                              endDate: stage?.endDate || undefined,
                              dayQuantity: stage.dayQuantity || 0,

                              nextYearsFrom: +stage.nextYearsFrom || undefined,
                              nextYearsTo: +stage.nextYearsTo || undefined,
                              yearsCount: +stage.yearsCount || undefined,

                              geometry: stage?.geometry?.length ? JSON.stringify(stage?.geometry) : undefined,

                              roadName: stage.roadName,
                              roadNumber: stage.roadNumber,
                              street: stage.street || '',

                              parcels: parcels.map(v => v?.uuid || '') || [],
                              purposeFees: purposeFees.map(v => v?.uuid || '') || [],

                              ordinalNumber: index + 1,
                              roadOccupation: occupation?.uuid
                            }
                          })
                        )
                      );
                    })
                  ))
                );
              })
            );
          })
        )
      ),
      switchMap(() => this.applicationService.getApplicationItemApplicationItem({id: application?.uuid || ''}))
    );
  }
}
